import { useState } from "react";
import {
  Card,
  CardContent,
  CardHeader,
  CardTitle,
} from "@/components/ui/library/card";
import { Building2, GraduationCap, RefreshCw } from "lucide-react";
import { useEduChildModuleData } from "@/module/education/helpers/useEduChildModuleData";
import ChangeUniversityModal from "@/features/leads/components/view-lead/components/Ongoing/components/ChangeUniversityModal";
import { getNicheEntry, type NicheViewProps } from "../nicheRegistry";
import { dealStageIdOf, useCanEditDeal } from "@/rbac/useCanEditDeal";

export default function CourseAttachedView({
  child,
  connector,
}: NicheViewProps) {
  const { universityName, courseName } = useEduChildModuleData(child);
  const [changeOpen, setChangeOpen] = useState(false);
  const canEditDeal = useCanEditDeal();

  const label =
    getNicheEntry(connector.nicheModelName)?.attachLabel ||
    connector.connectionLabel ||
    "Course";

  return (
    <>
    <Card className="w-full gap-0 border border-[#e6e8e7] shadow-none rounded-[14px] bg-white overflow-hidden py-0">
      <CardHeader className="px-4 py-3 pb-0">
        <div className="flex items-center justify-between">
          <CardTitle className="text-[16px] font-semibold text-[#051912] tracking-[-0.32px] leading-normal">
            {label}
          </CardTitle>
          {/* University changer — re-links the deal to a course of the chosen
              university (scoped to the deal's country). Only meaningful once a
              course is attached, and only for roles that can "Edit choices"
              on the deal's own stage. */}
          {courseName && canEditDeal(dealStageIdOf(child)) && (
            <button
              type="button"
              onClick={() => setChangeOpen(true)}
              className="flex items-center gap-1.5 text-[12px] font-medium text-(--color-success) hover:underline shrink-0"
            >
              <RefreshCw className="size-3.5" />
              Change university
            </button>
          )}
        </div>
      </CardHeader>

      <CardContent className="p-0 pb-3">
        <div className="px-4 py-2.5">
          <div className="flex flex-col gap-2">
            {universityName && (
              <div className="flex items-center gap-2 min-w-0">
                <Building2 className="size-4 text-[#697571] shrink-0" />
                <div className="flex flex-col min-w-0">
                  <p className="text-[12px] text-[#8a918d] tracking-[-0.22px] truncate">
                    University
                  </p>
                  <p className="text-[14px] font-semibold text-[#051912] tracking-[-0.28px] truncate">
                    {universityName}
                  </p>
                </div>
              </div>
            )}
            {courseName && (
              <div className="flex items-center gap-2 min-w-0">
                <GraduationCap className="size-4 text-[#697571] shrink-0" />
                <div className="flex flex-col min-w-0">
                  <p className="text-[12px] text-[#8a918d] tracking-[-0.22px] truncate">
                    Course
                  </p>
                  <p className="text-[14px] font-semibold text-[#051912] tracking-[-0.28px] truncate">
                    {courseName}
                  </p>
                </div>
              </div>
            )}
          </div>
        </div>
      </CardContent>
    </Card>

      <ChangeUniversityModal
        open={changeOpen}
        onClose={() => setChangeOpen(false)}
        dealId={child.id}
        dealName={child.name || "Deal"}
        currentUniversityName={universityName}
      />
    </>
  );
}
